import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationContext = React.createContext(null);

const NATIVE_TOKEN_KEY = "fripri_native_push_token";

function getNativePushBridge() {
  return window.webkit?.messageHandlers?.fripriPush || null;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [permission, setPermission] = useState(() => {
    if (typeof window === "undefined") return "default";
    if (localStorage.getItem(NATIVE_TOKEN_KEY)) return "granted";
    return "Notification" in window ? Notification.permission : "default";
  });

  const refresh = useCallback(async () => {
    if (!user) { setNotifications([]); setUnread(0); return; }
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  }, [user]);

  useEffect(() => {
    refresh();
    if (!user) return;
    const id = setInterval(refresh, 25000);
    return () => clearInterval(id);
  }, [refresh, user]);

  // Le pont natif iOS renvoie le token APNs à chaque lancement : on le rafraîchit côté serveur.
  useEffect(() => {
    if (!user) return;
    const onToken = async (e) => {
      const token = e.detail;
      if (!token) return;
      try {
        await api.post("/notifications/subscribe-native", { token, platform: "ios" });
        localStorage.setItem(NATIVE_TOKEN_KEY, token);
        setPermission("granted");
      } catch {}
    };
    window.addEventListener("fripri-push-token", onToken);
    return () => window.removeEventListener("fripri-push-token", onToken);
  }, [user]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read");
      setUnread(0);
      setNotifications((arr) => arr.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const subscribeNative = () =>
    new Promise((resolve) => {
      let timer;
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener("fripri-push-token", onToken);
        window.removeEventListener("fripri-push-denied", onDenied);
        window.removeEventListener("fripri-push-error", onError);
      };
      const onToken = async (e) => {
        cleanup();
        try {
          await api.post("/notifications/subscribe-native", { token: e.detail, platform: "ios" });
          localStorage.setItem(NATIVE_TOKEN_KEY, e.detail);
          setPermission("granted");
          resolve({ ok: true });
        } catch {
          resolve({ ok: false, reason: "server" });
        }
      };
      const onDenied = () => {
        cleanup();
        setPermission("denied");
        resolve({ ok: false, reason: "denied" });
      };
      const onError = () => {
        cleanup();
        resolve({ ok: false, reason: "native-error" });
      };
      timer = setTimeout(() => { cleanup(); resolve({ ok: false, reason: "timeout" }); }, 30000);
      window.addEventListener("fripri-push-token", onToken);
      window.addEventListener("fripri-push-denied", onDenied);
      window.addEventListener("fripri-push-error", onError);
      getNativePushBridge().postMessage("subscribe");
    });

  const subscribePush = async () => {
    if (getNativePushBridge()) return subscribeNative();

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return { ok: false, reason: "unsupported" };
    }
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== "granted") return { ok: false, reason: "denied" };

    const registration = await navigator.serviceWorker.ready;
    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }
    const json = sub.toJSON();
    await api.post("/notifications/subscribe", { endpoint: json.endpoint, keys: json.keys });
    return { ok: true };
  };

  const unsubscribePush = async () => {
    const nativeToken = localStorage.getItem(NATIVE_TOKEN_KEY);
    if (nativeToken) {
      try {
        await api.post("/notifications/unsubscribe-native", { token: nativeToken, platform: "ios" });
      } catch {}
      localStorage.removeItem(NATIVE_TOKEN_KEY);
      setPermission("default");
      if (getNativePushBridge()) return;
    }
    if (!("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      await api.post("/notifications/unsubscribe", { endpoint: json.endpoint, keys: json.keys });
      await sub.unsubscribe();
    }
  };

  const value = { notifications, unread, refresh, markAllRead, subscribePush, unsubscribePush, permission };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  return React.useContext(NotificationContext);
}
